import {
  Alert,
  Card,
  Col,
  Collapse,
  Modal,
  Row,
  Table,
} from "antd";
import {
  AppstoreOutlined,
  InboxOutlined,
  StockOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { ReactNode, useState } from "react";
import {
  EstimateLine,
  EstimateResult,
  PALLET_TARE_KG,
  USABLE_PALLET_CBM,
} from "./types";
import QuantityDetailModal from "./QuantityDetailModal";
import {
  formatMissingSkuMessage,
  hasMissingDimensionData,
  hasMissingWeightData,
  IncompleteZero,
  isIncompleteZero,
} from "./estimateUtils";
import styles from "./index.module.scss";

interface KpiCardProps {
  icon: ReactNode;
  label: string;
  value: string;
  hint: string;
}

const KpiCard = ({ icon, label, value, hint }: KpiCardProps) => (
  <Card className={styles.kpiCard}>
    <div className={styles.kpiLabelRow}>
      <span className={styles.kpiIcon}>{icon}</span>
      <span className={styles.kpiLabel}>{label}</span>
    </div>
    <div className={styles.kpiValue}>{value}</div>
    <div className={styles.kpiHint}>{hint}</div>
  </Card>
);

interface EstimateResultModalProps {
  open: boolean;
  result: EstimateResult | null;
  onClose: () => void;
}

const EstimateResultModal = ({
  open,
  result,
  onClose,
}: EstimateResultModalProps) => {
  const [quantityDetailLine, setQuantityDetailLine] =
    useState<EstimateLine | null>(null);

  if (!result) {
    return null;
  }

  const handleEstimateModalClose = () => {
    setQuantityDetailLine(null);
    onClose();
  };

  const missingWeight = hasMissingWeightData(
    result.estimate.missing,
    result.lines,
  );
  const missingDimensions = hasMissingDimensionData(
    result.estimate.missing,
    result.lines,
  );
  const weightZeroIncomplete = isIncompleteZero(
    result.estimate.productWeightKg,
    missingWeight,
  );
  const volumeZeroIncomplete = isIncompleteZero(
    result.estimate.totalCbm,
    missingDimensions,
  );

  const orderLabels = result.salesOrders.map((order) => order.refNum).join(", ");

  const tableColumns = [
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      render: (text: string) => <span className={styles.monoCell}>{text}</span>,
    },
    {
      title: "Product",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 64,
          align: "right" as const,
      render: (quantity: number, record: EstimateLine) =>
        record.quantitySources?.length > 1 ? (
          <span
            className={styles.quantityLink}
            onClick={() => setQuantityDetailLine(record)}
          >
            {quantity}
          </span>
        ) : (
          quantity
        ),
    },
    {
      title: "Unit Wt (kg)",
      dataIndex: "weightKg",
      key: "weightKg",
      align: "right" as const,
      render: (value: number | null, record: EstimateResult["lines"][0]) =>
        record.missingItemModel || record.missingWeight ? (
          <IncompleteZero decimals={2} />
        ) : (
          value?.toFixed(2)
        ),
    },
    {
      title: "Dimensions (in)",
      key: "dimensions",
      align: "right" as const,
      render: (_: unknown, record: EstimateResult["lines"][0]) =>
        record.missingItemModel || record.missingDimensions ? (
          <span className={styles.incompleteDimensions}>0 × 0 × 0</span>
        ) : (
          `${record.length} × ${record.width} × ${record.height}`
        ),
    },
    {
      title: "Line Wt (kg)",
      dataIndex: "lineWeightKg",
      key: "lineWeightKg",
      align: "right" as const,
      render: (
        value: number | null,
        record: EstimateResult["lines"][0],
      ) =>
        record.missingItemModel || record.missingWeight ? (
          <IncompleteZero decimals={2} />
        ) : value != null ? (
          value.toFixed(2)
        ) : (
          "—"
        ),
    },
    {
      title: "Line Vol (CBM)",
      dataIndex: "lineCbm",
      key: "lineCbm",
      align: "right" as const,
      render: (
        value: number | null,
        record: EstimateResult["lines"][0],
      ) =>
        record.missingItemModel || record.missingDimensions ? (
          <IncompleteZero decimals={3} />
        ) : value != null ? (
          value.toFixed(3)
        ) : (
          "—"
        ),
    },
  ];

  return (
    <>
    <Modal
      open={open}
      onCancel={handleEstimateModalClose}
      footer={null}
      centered
      width={1500}
      wrapClassName={styles.estimateModalWrap}
      className={styles.estimateModal}
      title="Shipment Estimate"
      destroyOnClose
      maskClosable={false}
    >
      <p className={styles.modalSubtitle}>
        {result.customer.name} · {result.salesOrders.length} sales order
        {result.salesOrders.length > 1 ? "s" : ""} ({orderLabels})
      </p>

      <Row gutter={[16, 16]} className={styles.kpiRow}>
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            icon={<StockOutlined />}
            label="Total Product Weight"
            value={`${result.estimate.productWeightKg.toFixed(2)} kg`}
            hint={`${result.lines.length} SKUs`}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            icon={<AppstoreOutlined />}
            label="Estimated Volume"
            value={`${result.estimate.totalCbm.toFixed(3)} CBM`}
            hint={`${Math.round(result.estimate.totalCubicInches).toLocaleString()} in³`}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            icon={<TableOutlined />}
            label="Estimated Pallets"
            value={String(result.estimate.pallets)}
            hint={`${USABLE_PALLET_CBM.toFixed(2)} CBM per pallet`}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            icon={<InboxOutlined />}
            label="Final Shipment Weight"
            value={`${result.estimate.finalWeightKg.toFixed(2)} kg`}
            hint={`Incl. ${result.estimate.palletWeightKg.toFixed(2)} kg pallet weight`}
          />
        </Col>
      </Row>

      {result.estimate.missing.length > 0 && (
        <Alert
          type="warning"
          showIcon
          className={styles.warningAlert}
          message="Missing product data"
          description={
            <>
              <div>
                The estimate may be incomplete. Line items with missing weight or
                dimensions are shown in{" "}
                <strong style={{ color: "#cf1322" }}>red</strong> in the table
                below. The following SKUs need attention:
              </div>
              <ul className={styles.warningList}>
                {result.estimate.missing.map((item) => (
                  <li key={item.sku}>
                    <strong>{item.sku}</strong> —{" "}
                    {formatMissingSkuMessage(item.reasons)}
                  </li>
                ))}
              </ul>
            </>
          }
        />
      )}

      <Card className={styles.detailCard}>
        <div className={styles.tableSectionHeader}>
          <div>
            <div className={styles.soHeaderLabel}>Combined Line Items</div>
            <div className={styles.tableSectionHint}>
              {result.lines.length} unique SKUs across selected orders
            </div>
          </div>
        </div>

        <Table
          dataSource={result.lines}
          columns={tableColumns}
          rowKey={(record) => `${record.sku}-${record.name}`}
          pagination={false}
          className={`table-global ${styles.estimateTable}`}
          tableLayout="fixed"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row className={styles.summaryRow}>
                <Table.Summary.Cell index={0} colSpan={5}>
                  Totals
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <span
                    className={
                      weightZeroIncomplete ? styles.summaryIncomplete : ""
                    }
                  >
                    {result.estimate.productWeightKg.toFixed(2)} kg
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <span
                    className={
                      volumeZeroIncomplete ? styles.summaryIncomplete : ""
                    }
                  >
                    {result.estimate.totalCbm.toFixed(3)} CBM
                  </span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>

      {result.salesOrders.length > 1 && (
        <p className={styles.estimateHint}>
          When the same product appears on more than one of your selected sales
          orders, the combined quantity is shown in{" "}
          <span className={styles.quantityLinkSample}>blue</span>. Click it to see the quantity breakdown by sales order.
        </p>
      )}

      <Collapse
        className={styles.helpCollapse}
        items={[
          {
            key: "how",
            label: "How this is calculated",
            children: (
              <div className={styles.helpContent}>
                <p>
                  <strong>Total Weight</strong> = Σ (unit weight × qty) for
                  every SKU with weight data.
                </p>
                <p>
                  <strong>Volume (CBM)</strong> = Σ (L × W × H × qty) in
                  cubic inches × 0.000016387064.
                </p>
                <p>
                  <strong>Pallets</strong> = ⌈Total CBM ÷{" "}
                  {USABLE_PALLET_CBM.toFixed(2)}⌉, using a 48″ × 40″ pallet
                  with 60″ usable stacking height.
                </p>
                <p>
                  <strong>Final Weight</strong> = Product weight + (pallets ×{" "}
                  {PALLET_TARE_KG} kg).
                </p>
              </div>
            ),
          },
        ]}
      />
    </Modal>

    <QuantityDetailModal
      open={quantityDetailLine !== null}
      line={quantityDetailLine}
      onClose={() => setQuantityDetailLine(null)}
    />
    </>
  );
};

export default EstimateResultModal;
